import {
  Divider,
  Form,
  Header,
  InputLabel,
  Space,
  SubText,
  Text,
  TextField,
} from '@procurify/ui'
import { useField } from 'formik'

import { useProcurifyIntl } from '@webapp/hooks'
import { formatInboxEmailAddressPreview } from './formatInboxEmailAddressPreview'
import messages from './messages'
import { FieldRowStyled } from './styles/FieldRowStyled'

export interface IInboxAddressSectionProps {
  /**
   * The inbox handle is set once at creation, so an existing entity shows its
   * inbox address read-only instead of the inbox name input.
   */
  disabled: boolean
  /** The entity's inbox address, shown when `disabled` is set. */
  currentInboxEmailAddress: string
  /**
   * Domain name, used as a prefix for the inbox email address.
   */
  domain: string
}

const INBOX_HANDLE_MAX_LENGTH = 16

export const InboxAddressSection = ({
  disabled,
  currentInboxEmailAddress,
  domain,
}: IInboxAddressSectionProps) => {
  const intl = useProcurifyIntl()
  const [{ value: inboxHandleValue }] = useField<string>('inboxHandle')

  const previewInboxEmailAddress = formatInboxEmailAddressPreview(
    domain,
    inboxHandleValue
  )

  return (
    <Space direction='vertical' size='small'>
      <Divider />
      <Header as='h5' gutter>
        {intl.formatMessage(messages.inboxSectionHeader)}
      </Header>
      <Text>{intl.formatMessage(messages.inboxSectionDescription)}</Text>
      {disabled ? (
        <TextField
          id='inboxEmailAddress'
          value={currentInboxEmailAddress}
          disabled
          fullWidth
          inputProps={{
            'aria-label': intl.formatMessage(messages.inboxSectionHeader),
          }}
        />
      ) : (
        <>
          <FieldRowStyled>
            <InputLabel htmlFor='inboxHandle'>
              {intl.formatMessage(messages.inboxHandleLabel)}
            </InputLabel>
            <Space direction='horizontal' size='medium' align='start'>
              <Space.Item width={1 / 2}>
                <Form.TextField
                  id='inboxHandle'
                  name='inboxHandle'
                  inputProps={{
                    maxLength: INBOX_HANDLE_MAX_LENGTH,
                  }}
                  helperText={intl.formatMessage(
                    messages.inboxHandleCharacterCount,
                    {
                      count: inboxHandleValue.length,
                      max: INBOX_HANDLE_MAX_LENGTH,
                    }
                  )}
                />
              </Space.Item>
              <Space.Item width={1 / 2}>
                <SubText color='textSecondary'>
                  {intl.formatMessage(messages.inboxHandleTip)}
                </SubText>
              </Space.Item>
            </Space>
          </FieldRowStyled>
          {previewInboxEmailAddress && (
            <Text>
              {intl.formatMessage(messages.inboxAddressRecap, {
                address: (
                  <Text inline bold>
                    {previewInboxEmailAddress}
                  </Text>
                ),
              })}
            </Text>
          )}
        </>
      )}
    </Space>
  )
}

InboxAddressSection.displayName = 'InboxAddressSection'
